/**
 * skyViewNode — v2 sky background TSL node that samples the baked sky-view
 * LUT instead of ray-marching per fragment (Hillaire 2020 §6 consumption).
 *
 * For each background fragment:
 *   1. Take the view direction V = `positionWorldDirection` (translation-invariant).
 *   2. Compute µ = dot(V, up) and cosLightView = dot(V, L) (sun-relative
 *      azimuth) — the same two coordinates the CPU `skyViewUV` consumes.
 *   3. Map (µ, cosLightView) → LUT UV inside the shader (mirroring the CPU
 *      `skyViewUV` math) and sample the sky-view LUT via `texture()`.
 *   4. Add an analytic sun disc (kept crisp independent of LUT resolution):
 *      a smoothstep around `dot(V, L) > cos(sunAngularRadius)`, scaled by
 *      sun illuminance.
 *
 * The non-linear zenith remap here MUST match `skyViewUV` so the GPU samples
 * the exact texels the CPU bake wrote. The azimuth remap is the squared form
 * `u = 0.5 + 0.5·sign(c)·sqrt(|c|)`.
 *
 * This node is a drop-in replacement for `SkyRenderer.buildBackgroundNode()`
 * when `pipeline: 'lut'`. It is ~1 texture fetch + a sun-disc test per
 * fragment, vs the v1 inline march's O(numSamples·sunSamples) per fragment.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, vec3, vec4, float, uniform,
  dot, normalize, sqrt, abs, sign, max, smoothstep, acos, cos, clamp, mix, exp, atan,
  positionWorldDirection, texture,
} from 'three/tsl';

export interface SkyViewNodeUniforms {
  /** Sun direction (FROM origin TO sun), world space. */
  sunDirection: ReturnType<typeof uniform>;
  /** Sun illuminance multiplier for the disc. */
  sunIlluminance: ReturnType<typeof uniform>;
  /** Sun disc angular radius (radians). ~0.0047 for the real sun. */
  sunAngularRadius: ReturnType<typeof uniform>;
  /** Planet bottom radius (km) — for the horizon zenith remap. */
  bottomRadius: ReturnType<typeof uniform>;
  /** Camera radius from planet centre (km). */
  viewHeightKm: ReturnType<typeof uniform>;
  /**
   * Atmospheric transmittance toward the sun (RGB), CPU-evaluated per frame
   * from the SAME atmosphere config (transmittanceToTopOfAtmosphere). This is
   * what turns the disc deep orange at the horizon and near-white at noon —
   * a flat white disc at sunset was the old look's biggest realism miss.
   */
  sunTint: ReturnType<typeof uniform>;
  /**
   * The PHYSICAL-LIGHT hooks (2026-09-25, Atlas realism §8 R1/R2). Defaults
   * are neutral (1 / 1 / 0), so a caller that never writes them keeps the old
   * sky exactly.
   *   radianceScale — multiplies the whole sky (LUT + disc): a caller that bakes
   *                   the LUT at sunIlluminance 1 carries its exposure-scaled
   *                   illuminance here, continuously, without a LUT re-bake.
   *   discScale     — the disc alone (a physical disc is ~10⁴× the sky; the
   *                   caller picks what its HDR targets can hold).
   *   overcastMix   — 0..1 blend of the whole dome toward a CIE overcast
   *                   gradient (zenith 3× the horizon) of `overcastZenith`
   *                   radiance, `overcastGround` below the horizon; the disc
   *                   fades with it. Values are in the node's OUTPUT units.
   */
  radianceScale?: ReturnType<typeof uniform>;
  discScale?: ReturnType<typeof uniform>;
  overcastMix?: ReturnType<typeof uniform>;
  overcastZenith?: ReturnType<typeof uniform>;
  overcastGround?: ReturnType<typeof uniform>;
  /**
   * THE BAKED NIGHT (2026-10-06): an ADDITIVE night sky for the IBL cube — the LUT and the inline march carry the SUN's
   * light only, so at night the environment cube was black and no shiny surface reflected the night sky. Zero outside a
   * bake (SkyRenderer swaps them in with the bake overrides' `night`), so the visible dome is the old one bit for bit.
   *   nightZenith / nightHorizon — the gradient (horizon weight (1 − sin elevation)³);
   *   nightGround — below the horizon (the ground's bounce);
   *   nightDome — vec4(dirX, dirZ, halfWidth rad, height rad): one light dome's bearing and size;
   *   nightDomeColor — the dome's excess radiance at the horizon.
   * `nightBakeRadianceCpu` is the mirror; `nightBakeIrradianceCpu` integrates it over an up-facing surface.
   */
  nightZenith?: ReturnType<typeof uniform>;
  nightHorizon?: ReturnType<typeof uniform>;
  nightGround?: ReturnType<typeof uniform>;
  nightDome?: ReturnType<typeof uniform>;
  nightDomeColor?: ReturnType<typeof uniform>;
}

export function createSkyViewNodeUniforms(opts: {
  sunIlluminance: number;
  bottomRadius: number;
  viewHeightKm: number;
  sunAngularRadius?: number;
}): SkyViewNodeUniforms {
  return {
    sunDirection: uniform(vec3(0, 1, 0)),
    sunIlluminance: uniform(opts.sunIlluminance),
    sunAngularRadius: uniform(opts.sunAngularRadius ?? 0.0047),
    bottomRadius: uniform(opts.bottomRadius),
    viewHeightKm: uniform(opts.viewHeightKm),
    sunTint: uniform(vec3(1, 1, 1)),
    radianceScale: uniform(1),
    discScale: uniform(1),
    overcastMix: uniform(0),
    overcastZenith: uniform(vec3(0, 0, 0)),
    overcastGround: uniform(vec3(0, 0, 0)),
    nightZenith: uniform(vec3(0, 0, 0)),
    nightHorizon: uniform(vec3(0, 0, 0)),
    nightGround: uniform(vec3(0, 0, 0)),
    nightDome: uniform(vec4(1, 0, 0.5, 0.1)),
    nightDomeColor: uniform(vec3(0, 0, 0)),
  };
}

/** THE BAKED NIGHT's radiance along a unit direction `V` (see SkyViewNodeUniforms.nightZenith). Zero when every input is 0. */
export function nightBakeRadianceNode(V: unknown, zenith: unknown, horizon: unknown, ground: unknown, dome: unknown, domeColor: unknown): unknown {
  const v = vec3(V as never);
  const d = vec4(dome as never);
  const mu = clamp(v.y, -1, 1);
  const s = max(mu, 0);
  const c = float(1).sub(s);
  const grad = mix(vec3(zenith as never), vec3(horizon as never), c.mul(c).mul(c));
  const h = sqrt(max(v.x.mul(v.x).add(v.z.mul(v.z)), 1e-8));
  const elev = atan(s, h);
  const cosAz = clamp(v.x.mul(d.x).add(v.z.mul(d.y)).div(h), -1, 1);
  const dAz = acos(cosAz).div(max(d.z, 1e-3));
  const domeL = vec3(domeColor as never).mul(exp(dAz.mul(dAz).mul(-0.5))).mul(exp(elev.negate().div(max(d.w, 1e-3))));
  return mix(vec3(ground as never), grad.add(domeL), smoothstep(-0.02, 0.02, mu));
}

export interface NightBakeParams {
  zenith: readonly [number, number, number];
  horizon: readonly [number, number, number];
  ground: readonly [number, number, number];
  /** [dirX, dirZ, halfWidth rad, height rad]. */
  dome: readonly [number, number, number, number];
  domeColor: readonly [number, number, number];
}
/** CPU mirror of nightBakeRadianceNode (one RGB triple into `out`). */
export function nightBakeRadianceCpu(vx: number, vy: number, vz: number, p: NightBakeParams, out: [number, number, number] = [0, 0, 0]): [number, number, number] {
  const mu = Math.max(-1, Math.min(1, vy));
  const s = Math.max(mu, 0), c = 1 - s, w = c * c * c;
  const h = Math.sqrt(Math.max(vx * vx + vz * vz, 1e-8));
  const elev = Math.atan2(s, h);
  const cosAz = Math.max(-1, Math.min(1, (vx * p.dome[0] + vz * p.dome[1]) / h));
  const dAz = Math.acos(cosAz) / Math.max(p.dome[2], 1e-3);
  const dw = Math.exp(-0.5 * dAz * dAz) * Math.exp(-elev / Math.max(p.dome[3], 1e-3));
  const t = Math.max(0, Math.min(1, (mu + 0.02) / 0.04)), k = t * t * (3 - 2 * t);
  for (let i = 0; i < 3; i++) {
    const sky = p.zenith[i] + (p.horizon[i] - p.zenith[i]) * w + p.domeColor[i] * dw;
    out[i] = p.ground[i] + (sky - p.ground[i]) * k;
  }
  return out;
}
/** Irradiance (RGB) of THE BAKED NIGHT on an up-facing surface: ∫ L cosθ dΩ over the upper hemisphere (24 × 48 midpoint quadrature). */
export function nightBakeIrradianceCpu(p: NightBakeParams): [number, number, number] {
  const NT = 24, NP = 48, dT = (Math.PI / 2) / NT, dP = (2 * Math.PI) / NP;
  const e: [number, number, number] = [0, 0, 0], L: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < NT; i++) {
    const th = (i + 0.5) * dT, ct = Math.cos(th), st = Math.sin(th), wgt = ct * st * dT * dP;
    for (let j = 0; j < NP; j++) {
      const ph = (j + 0.5) * dP;
      nightBakeRadianceCpu(st * Math.cos(ph), ct, st * Math.sin(ph), p, L);
      e[0] += L[0] * wgt; e[1] += L[1] * wgt; e[2] += L[2] * wgt;
    }
  }
  return e;
}

/**
 * The CIE overcast dome (Moon & Spencer): L(θ) = Lz·(1 + 2·cosθ)/3 above the
 * horizon, a flat ground radiance below, blended over ±1.2° so the horizon
 * does not step. `mu` is the view ray's cosine with up.
 */
export function overcastRadianceNode(mu: unknown, zenith: unknown, ground: unknown): unknown {
  const m = float(mu as never);
  const sky = vec3(zenith as never).mul(float(1).add(m.max(0).mul(2)).div(3));
  return mix(vec3(ground as never), sky, smoothstep(-0.02, 0.02, m));
}
/** CPU mirror of overcastRadianceNode for one channel (tests / harnesses). */
export function overcastRadianceCpu(mu: number, zenith: number, ground: number): number {
  const t = Math.max(0, Math.min(1, (mu + 0.02) / 0.04));
  const s = t * t * (3 - 2 * t);
  return ground + (zenith * (1 + 2 * Math.max(0, mu)) / 3 - ground) * s;
}

/**
 * Build the v2 background node. `skyViewTexture` is the baked sky-view LUT
 * DataTexture (Float RGBA, 192×108). The node returns a vec4 radiance.
 */
export function buildSkyViewBackgroundNode(
  skyViewTexture: THREE.DataTexture,
  u: SkyViewNodeUniforms,
): unknown {
  const evalFn = Fn(() => {
    // WHY (the "sky slides when the player moves" bug): the background sphere
    // has an IDENTITY matrixWorld (three common/Background.js), so positionWorld
    // is already a unit direction — subtracting cameraPosition swung every ray
    // with camera translation. positionWorldDirection is translation-invariant.
    const V = positionWorldDirection;
    const L = normalize(u.sunDirection);
    const up = vec3(0, 1, 0);

    const mu = dot(V, up).clamp(-1, 1);          // cosViewZenith
    const cosLightView = dot(V, L).clamp(-1, 1); // sun-relative azimuth

    // ── Zenith remap (mirror of skyViewUV, two-half sqrt around horizon) ──
    const r = max(u.viewHeightKm, u.bottomRadius.add(1e-3));
    const vHoriz = sqrt(max(float(0), r.mul(r).sub(u.bottomRadius.mul(u.bottomRadius))));
    const groundToHorizon = acos((vHoriz.div(r)).clamp(-1, 1));
    const zenithToHorizon = float(Math.PI).sub(groundToHorizon);
    const theta = acos(mu);

    // Upper half: v = 0.5·(1 − sqrt(1 − θ/Z)).  Lower half: v = 0.5 + 0.5·sqrt((θ−Z)/G).
    const upperCoord = float(1).sub(theta.div(zenithToHorizon));
    const upperV = float(0.5).mul(float(1).sub(sqrt(max(upperCoord, float(0)))));
    const lowerCoord = theta.sub(zenithToHorizon).div(groundToHorizon);
    const lowerV = float(0.5).add(float(0.5).mul(sqrt(max(lowerCoord, float(0)))));
    const isUpper = theta.lessThan(zenithToHorizon);
    const vCoord = isUpper.select(upperV, lowerV);

    // ── Azimuth remap (squared, symmetric about sun) ──
    const uCoord = float(0.5)
      .add(float(0.5).mul(sign(cosLightView)).mul(sqrt(abs(cosLightView))));

    const lutColor = texture(skyViewTexture, vec3(uCoord, vCoord, 0).xy);
    const radianceScale = u.radianceScale ?? float(1);
    const overcastMix = u.overcastMix ?? float(0);
    const clearSky = vec3(lutColor.x, lutColor.y, lutColor.z).mul(radianceScale);
    const sky = u.overcastZenith && u.overcastGround
      ? mix(clearSky, vec3(overcastRadianceNode(mu, u.overcastZenith, u.overcastGround) as never), overcastMix)
      : clearSky;

    // sunTint = CPU-evaluated transmittance toward the sun: white at noon,
    // deep orange at the horizon, fading out below it.
    const sunContribution = vec3(sunDiscNode(V, L, u.sunAngularRadius, u.sunTint) as never).mul(u.sunIlluminance)
      .mul(u.discScale ?? float(1)).mul(radianceScale).mul(float(1).sub(overcastMix));

    // THE BAKED NIGHT: zero outside a bake (the uniforms are swapped in by SkyRenderer.beginBake).
    const night = u.nightZenith && u.nightHorizon && u.nightGround && u.nightDome && u.nightDomeColor
      ? vec3(nightBakeRadianceNode(V, u.nightZenith, u.nightHorizon, u.nightGround, u.nightDome, u.nightDomeColor) as never)
      : vec3(0, 0, 0);
    return vec4(sky.add(sunContribution).add(night), 1.0);
  });

  return evalFn();
}

/**
 * The analytic sun disc both sky pipelines draw (limb-darkened, transmittance-
 * tinted, unscaled): `tint · disc · limb`. A plain graph-building function (the
 * atmosphereFn GLSL note — no Fn() argument smuggling). Moved out of
 * buildSkyViewBackgroundNode on 2026-09-26 so the inline march's LUT parity
 * draws the SAME disc (SkyRenderer `lutParity`).
 *   Visible where the view ray falls within the sun's angular radius:
 *   dot(V, L) > cos(radius), with a soft edge over a few ×10⁻⁴ in cosine so the
 *   rim anti-aliases instead of stair-stepping. Limb darkening (2026-09-27, the
 *   coordinator's review; was an achromatic µ^0.6): the linear law I(µ)/I(0) =
 *   1 − u·(1 − µ) with µ = cos of the emergence angle (r² across the disc from
 *   centre 0 to rim 1) and a PER-CHANNEL u — Neckel & Labs 1994 / Neckel 2005
 *   fit u ≈ 0.50 red, 0.60 visible, 0.72 blue: the rim darkens AND reddens (an
 *   orange edge at sunset through the haze). The disc's mean of the law over
 *   its area is 1 − u/3, so the factor is divided by it: the disc keeps the
 *   tint's whole energy (the meter and the bloom see the physical disc; the
 *   old power law carried 77 % of it). The rim itself sits at 1 − u (0.28–0.5),
 *   never 0 — a real limb is a soft edge, not a black ring.
 */
export function sunDiscNode(V: unknown, L: unknown, angularRadius: unknown, tint: unknown): unknown {
  const cosV_L = dot(vec3(V as never), vec3(L as never));
  const cosRadius = cos(float(angularRadius as never));
  const disc = smoothstep(cosRadius.sub(0.0005), cosRadius.add(0.0002), cosV_L);
  const r2 = clamp(
    float(1).sub(cosV_L).div(max(float(1).sub(cosRadius), 1e-7)),
    0, 1,
  );
  const muLimb = sqrt(max(float(1).sub(r2), float(0)));
  const u = vec3(0.50, 0.60, 0.72);
  const limb = vec3(1).sub(u.mul(float(1).sub(muLimb))).div(vec3(1).sub(u.div(3)));
  return vec3(tint as never).mul(disc).mul(limb);
}
